// components/ProductsManagement.js
import React, { useState, useEffect, useMemo } from "react";
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import {
  productsAdminApi,
  productsPublicApi,
} from "../utils/apiProducts";

import ProductList from "../components/ProductList";
import ProductDecorList from "../components/ProductDecorList";
import ProductDecorForm from "../components/ProductDecorForm";
import ProductForm from "../components/ProductForm";

import ProductImagesList from "../components/ProductImagesList";
import ProductImageForm from "../components/ProductImageForm";
import ProductImageModal from "../components/ProductImageModal";

import TisProductsBrowser from "../components/TisProductsBrowser";

function logProductsApiError(context, error) {
  console.error(context, {
    status: error?.response?.status,
    message: error?.message,
  });
}

export default function ProductsManagement() {
  const { t } = useTranslation();

  // UI
  const [activeTab, setActiveTab] = useState("products"); // products|decor|images|catalog|tis
  const [searchTerm, setSearchTerm] = useState("");

  // Shared paging/filter
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [source, setSource] = useState("all"); // all|tis|darex|pinoles

  // Loading / error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // PRODUCTS
  const [products, setProducts] = useState([]);
  const [productsTotal, setProductsTotal] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  // CATALOG (UNIFIED)
  const [catalog, setCatalog] = useState([]);
  const [catalogTotal, setCatalogTotal] = useState(null);

  // DECOR
  const [decorations, setDecorations] = useState([]);
  const [editingDecorItem, setEditingDecorItem] = useState(null);

  // IMAGES
  const [images, setImages] = useState([]);
  const [editingImage, setEditingImage] = useState(null);

  // IMAGE MODAL
  const [imagePreview, setImagePreview] = useState(null);
  const openImage = (src) => setImagePreview(src);
  const closeImage = () => setImagePreview(null);

  // -------------------------
  // FETCH FUNCTIONS
  // -------------------------

  async function fetchProducts({ resetPage } = {}) {
    setLoading(true);
    setError("");
    try {
      if (resetPage) setPage(1);

      const { data } = await productsAdminApi.get("/api/products", {
        params: {
          page: resetPage ? 1 : page,
          pageSize,
          search: searchTerm || undefined,
          source, // ako backend ignoriše - ok
          sort: "ManufacturerName",
          dir: "asc",
          fields: "all",
        },
      });

      const rows = Array.isArray(data) ? data : data?.data || [];
      const total = Array.isArray(data) ? null : (data?.total ?? null);

      setProducts(rows);
      setProductsTotal(total);
    } catch (e) {
      logProductsApiError("Error fetching products", e);
      setProducts([]);
      setProductsTotal(null);
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchCatalog({ resetPage } = {}) {
    setLoading(true);
    setError("");
    try {
      if (resetPage) setPage(1);

      const { data } = await productsPublicApi.get("/api/catalog/unified", {
        params: {
          page: resetPage ? 1 : page,
          pageSize,
          q: searchTerm || undefined,
          source, // all|tis|darex|pinoles
          sort: "title",
          dir: "asc",
        },
      });

      // fetchCatalog(...)
const rows = Array.isArray(data?.data) ? data.data : [];
setCatalog(rows);
setCatalogTotal(data?.total ?? null);


      setCatalogTotal(data?.total ?? null);
    } catch (e) {
      logProductsApiError("Error fetching catalog", e);
      setCatalog([]);
      setCatalogTotal(null);
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchDecorations() {
    setLoading(true);
    setError("");
    try {
      const { data } = await productsAdminApi.get("/api/products/decor", {
        params: { search: searchTerm || undefined },
      });
      setDecorations(Array.isArray(data) ? data : (data?.data || []));
    } catch (e) {
      logProductsApiError("Error fetching decor", e);
      setDecorations([]);
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchImages() {
    setLoading(true);
    setError("");
    try {
      const { data } = await productsAdminApi.get("/api/product/images", {
        params: { search: searchTerm || undefined, limit: 10000 },
      });
      setImages(Array.isArray(data) ? data : (data?.data || []));
    } catch (e) {
      logProductsApiError("Error fetching images", e);
      setImages([]);
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  // -------------------------
  // DEBOUNCED SEARCH
  // -------------------------
  const debouncedSearch = useMemo(
    () =>
      debounce(() => {
        // kada se menja search, reset na page 1 i refresh samo aktivni tab
        if (activeTab === "products") fetchProducts({ resetPage: true });
        if (activeTab === "catalog") fetchCatalog({ resetPage: true });
        if (activeTab === "decor") fetchDecorations();
        if (activeTab === "images") fetchImages();
      }, 350),
    
    [activeTab, pageSize, source, searchTerm]
  );

  useEffect(() => {
    debouncedSearch();
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch]);

  // -------------------------
  // TAB / PAGING / SOURCE triggers (bez debounce)
  // -------------------------
  useEffect(() => {
    // kad promeniš tab → reset page i učitaj
    setPage(1);

    if (activeTab === "products") fetchProducts({ resetPage: true });
    if (activeTab === "catalog") fetchCatalog({ resetPage: true });
    if (activeTab === "decor") fetchDecorations();
    if (activeTab === "images") fetchImages();
    // tis tab nema fetch ovde
    
  }, [activeTab]);

  useEffect(() => {
    // page/pageSize/source utiču samo na paginated tabove
    if (activeTab === "products") fetchProducts();
    if (activeTab === "catalog") fetchCatalog();
    
  }, [page, pageSize, source]);

  // -------------------------
  // PRODUCTS handlers
  // -------------------------
  async function handleEditProduct(row) {
    try {
      setLoading(true);
      const { data } = await productsAdminApi.get(`/api/products/${encodeURIComponent(row.id)}`);
      setEditingProduct(data);
    } catch (e) {
      logProductsApiError("Error opening product", e);
      setEditingProduct(row);
    } finally {
      setLoading(false);
    }
  }

  async function handleEditCatalog(row) {
  try {
    setLoading(true);

    console.log("EDIT CATALOG ROW:", row);

    const rawId = String(row.id || "");
    if (!rawId.includes(":")) {
      throw new Error(`Invalid catalog id: ${rawId}`);
    }

    const { data } = await productsPublicApi.get(
      `/api/catalog/item/${encodeURIComponent(rawId)}`
    );

    setEditingProduct({
  ...data,

  catalogMode: true,
  catalogId: rawId,
  ExternalID: rawId,

  DistributorName: data.title || "",
  DistributorCode: data.sku || "",
  ManufacturerName: data.brand || "",
  Manufacturer: data.brand || "",
  DecorCode: data.decor || "",
  CurrentPrice: data.price ?? "",
  PriceCurrency: data.currency || "",
  UnitOfMeasure: data.unit || "",
  URL: data.url || "",
  Structure: data.source || "",
  Importer: data.source || "",
});
  } catch (e) {
    logProductsApiError("Catalog edit failed", e);
    alert("Cannot open material: " + (e?.response?.data?.error || e.message));
  } finally {
    setLoading(false);
  }
}

  function handleCreateProduct() {
    setEditingProduct({});
  }

async function handleSaveProduct(payload) {
  try {
    setLoading(true);

    const payloadId = String(payload.id || payload.catalogId || payload.ExternalID || "");
    const isCatalogItem =
      payload.catalogMode === true ||
      payloadId.includes(":") ||
      String(payload.ExternalID || "").includes(":");

    if (isCatalogItem) {
      const catalogId = payload.catalogId || payload.ExternalID || payload.id;

      await productsAdminApi.put(
        `/api/catalog/item/${encodeURIComponent(catalogId)}`,
        payload
      );

      await fetchCatalog();
      setEditingProduct(null);

      alert("Material saved.");
      return;
    }

    if (payload.id) {
      await productsAdminApi.put(
        `/api/products/${encodeURIComponent(payload.id)}`,
        payload
      );
    } else {
      const { data } = await productsAdminApi.post(`/api/products`, payload);
      payload.id = data?.id;
    }

    await fetchProducts();
    setEditingProduct(null);
  } catch (e) {
    logProductsApiError("Save failed", e);
    alert("Save failed: " + (e?.response?.data?.error || e.message));
  } finally {
    setLoading(false);
  }
}

  async function handleDuplicateProduct(row) {
    try {
      setLoading(true);
      const { data } = await productsAdminApi.get(`/api/products/${encodeURIComponent(row.id)}`);
      const copy = { ...data };

      delete copy.id;
      if ("ExternalID" in copy) copy.ExternalID = null;

      if (copy.DistributorName) {
        copy.DistributorName = `${copy.DistributorName} (copy)`;
      }

      setEditingProduct(copy);
    } catch (e) {
      logProductsApiError("Duplicate prep failed", e);
      alert("Cannot duplicate: " + (e?.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteProduct(id) {
    if (!window.confirm("Delete this product?")) return;
    try {
      setLoading(true);
      await productsAdminApi.delete(`/api/products/${encodeURIComponent(id)}`);
      await fetchProducts();
    } catch (e) {
      logProductsApiError("Delete product failed", e);
      alert("Delete failed: " + (e?.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }

  // -------------------------
  // DECOR handlers
  // -------------------------
  async function handleEditDecor(row) {
    try {
      setLoading(true);
      const { data } = await productsAdminApi.get(`/api/products/decor/${encodeURIComponent(row.id)}`);
      setEditingDecorItem(data);
    } catch (e) {
      logProductsApiError("Error opening decor", e);
      setEditingDecorItem(row);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveDecor(decor, file) {
    try {
      setLoading(true);

      let id = decor.id;
      if (id) {
        await productsAdminApi.put(`/api/products/decor/${encodeURIComponent(id)}`, decor);
      } else {
        const { data } = await productsAdminApi.post(`/api/products/decor`, decor);
        id = data?.id;
      }

      if (file && id) {
        const fd = new FormData();
        fd.append("image", file);
        await productsAdminApi.post(`/api/products/decor/${encodeURIComponent(id)}/image`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      await fetchDecorations();
      setEditingDecorItem(null);
    } catch (e) {
      logProductsApiError("Error saving decor", e);
      alert("Save failed: " + (e?.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteDecor(id) {
    if (!window.confirm("Delete this decor item?")) return;
    try {
      setLoading(true);
      await productsAdminApi.delete(`/api/products/decor/${encodeURIComponent(id)}`);
      await fetchDecorations();
    } catch (e) {
      logProductsApiError("Error deleting decor", e);
      alert("Delete failed: " + (e?.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }

  // -------------------------
  // IMAGES handlers
  // -------------------------
  async function handleEditImage(row) {
    try {
      setLoading(true);
      const { data } = await productsAdminApi.get(`/api/product/images/${encodeURIComponent(row.id)}`);
      setEditingImage(data);
    } catch (e) {
      logProductsApiError("Error opening image metadata", e);
      setEditingImage(row);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveImage(payload, file) {
    try {
      setLoading(true);

      let id = payload.id;
      if (id) {
        await productsAdminApi.put(`/api/product/images/${encodeURIComponent(id)}`, payload);
      } else {
        const { data } = await productsAdminApi.post(`/api/product/images`, payload);
        id = data?.id;
      }

      if (file && id) {
        const fd = new FormData();
        fd.append("image", file);
        await productsAdminApi.post(`/api/product/images/${encodeURIComponent(id)}/image`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      await fetchImages();
      setEditingImage(null);
    } catch (e) {
      logProductsApiError("Save image failed", e);
      alert("Save failed: " + (e?.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteImage(id) {
    if (!window.confirm("Delete this image row?")) return;
    try {
      setLoading(true);
      await productsAdminApi.delete(`/api/product/images/${encodeURIComponent(id)}`);
      await fetchImages();
    } catch (e) {
      logProductsApiError("Delete failed", e);
      alert("Delete failed: " + (e?.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }

  // -------------------------
  // DECOR filter (client-side)
  // -------------------------
  const filteredDecorations = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return decorations;
    return decorations.filter((d) =>
      [d.DecorCode, d.ManufacturerName, d.ColorAndCharacter, d.Structure]
        .some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [decorations, searchTerm]);

  // -------------------------
  // RENDER
  // -------------------------
  return (
    <div className="p-4 space-y-6">
      <h2 className="text-3xl font-bold text-primary-light dark:text-primary-dark">
        🛍️ {t("productsManagement.title")}
      </h2>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-neutral-700">
        {["products", "decor", "images", "catalog", "tis"].map((tab) => (
          <button
            key={tab}
            className={`px-4 py-2 font-medium max-w-full whitespace-normal break-words text-center leading-snug
              ${activeTab === tab
                ? "border-b-2 border-primary-light text-primary-light"
                : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            onClick={() => setActiveTab(tab)}
          >
            {t(`productsManagement.${tab}`)}
          </button>
        ))}
      </div>

      {/* Search + Source (radi za products/catalog, a ne smeta ostalima) */}
      <div className="flex items-center gap-4">
        <input
          type="text"
          placeholder="Search…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-3 py-2 border rounded dark:bg-neutral-800 dark:text-white w-full max-w-md"
        />

        <select
          value={source}
          onChange={(e) => { setSource(e.target.value); setPage(1); }}
          className="px-3 py-2 border rounded dark:bg-neutral-800 dark:text-white"
        >
          <option value="all">All sources</option>
          <option value="tis">TIS</option>
          <option value="darex">DAREX</option>
          <option value="pinoles">PINOLES</option>
        </select>

        <select
          value={pageSize}
          onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
          className="px-3 py-2 border rounded dark:bg-neutral-800 dark:text-white"
        >
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={200}>200</option>
        </select>
      </div>

      {error && (
        <div className="text-red-600 dark:text-red-400">
          Error: {error}
        </div>
      )}

      {/* PRODUCTS */}
      {activeTab === "products" && (
        editingProduct ? (
          <ProductForm
            initial={editingProduct}
            onCancel={() => setEditingProduct(null)}
            onSave={handleSaveProduct}
          />
        ) : (
          <ProductList
            products={products}
            loading={loading}
            globalFilter={searchTerm}
            page={page}
            pageSize={pageSize}
            total={productsTotal}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
            onEdit={handleEditProduct}
            onDelete={handleDeleteProduct}
            onCreate={handleCreateProduct}
            onDuplicate={handleDuplicateProduct}
          />
        )
      )}

      {/* CATALOG (UNIFIED - read-only) */}
      {activeTab === "catalog" && (
  editingProduct ? (
    <ProductForm
      initial={editingProduct}
      onCancel={() => setEditingProduct(null)}
      onSave={handleSaveProduct}
    />
  ) : (
    <ProductList
      products={catalog}
      loading={loading}
      globalFilter={searchTerm}
      page={page}
      pageSize={pageSize}
      total={catalogTotal}
      onPageChange={setPage}
      onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      onEdit={handleEditCatalog}
      onDelete={null}
      onCreate={null}
      onDuplicate={null}
    />
  )
)}

      {/* DECOR */}
      {activeTab === "decor" && (
        <div>
          {editingDecorItem ? (
            <ProductDecorForm
              decor={editingDecorItem}
              onSave={handleSaveDecor}
              onCancel={() => setEditingDecorItem(null)}
            />
          ) : (
            <>
              <button
                onClick={() => setEditingDecorItem({})}
                className="mb-4 bg-primary-light text-white px-4 py-2 rounded hover:bg-primary-dark"
              >
                {t("productsManagement.addDecor")}
              </button>

              <ProductDecorList
                decorations={filteredDecorations}
                onEdit={handleEditDecor}
                onDelete={handleDeleteDecor}
                loading={loading}
                onOpenImage={openImage}
              />
            </>
          )}
        </div>
      )}

      {/* IMAGES */}
      {activeTab === "images" && (
        editingImage ? (
          <ProductImageForm
            initial={editingImage}
            onCancel={() => setEditingImage(null)}
            onSave={handleSaveImage}
          />
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-gray-500">
                {images?.length ?? 0} images
              </div>
              <button
                onClick={() => setEditingImage({})}
                className="bg-primary-light text-white px-3 py-2 rounded hover:bg-primary-dark"
                title="Add new image"
              >
                + New image
              </button>
            </div>6s

            <ProductImagesList
              images={images}
              loading={loading}
              globalFilter={searchTerm}
              onEdit={handleEditImage}
              onDelete={handleDeleteImage}
              onAdd={() => setEditingImage({})}
              onOpenImage={openImage}
            />
          </>
        )
      )}

      {/* TIS */}
      {activeTab === "tis" && <TisProductsBrowser />}

      <ProductImageModal src={imagePreview} onClose={closeImage} />
    </div>
  );
}
